import React from "react";
import { X, MessageSquare } from "lucide-react";
import { BETA_NOTICE } from "@/lib/copy";
import { useBetaNotice } from "@/hooks/useBetaNotice";

/* ============================================================
   BetaNoticeBanner — dismissible "early access" callout.

   Floats in the top-right corner of the viewport after a short
   delay, so it doesn't flash in during initial load. role="status"
   (informational, not urgent). Dismissal persists via useBetaNotice.
   ============================================================ */

export function BetaNoticeBanner() {
  const { dismissed, dismiss } = useBetaNotice();
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    if (dismissed) return;
    const t = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(t);
  }, [dismissed]);

  if (dismissed) return null;

  const handleDismiss = () => {
    setVisible(false);
    setTimeout(() => {
      dismiss();
      document.getElementById("main-content")?.focus();
    }, 200);
  };

  return (
    <div
      role="status"
      data-beta-banner
      aria-live="polite"
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 60,
        maxWidth: 460,
        width: "min(460px, calc(100vw - 48px))",
        background: "var(--ink-0)",
        border: "1px solid var(--border-default)",
        borderRadius: 12,
        boxShadow: "0 8px 32px rgba(50,43,68,.18), 0 2px 8px rgba(50,43,68,.10)",
        padding: "14px 16px",
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(12px)",
        transition: "opacity .2s ease, transform .2s ease",
        pointerEvents: visible ? "auto" : "none",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: "var(--beta-bg)",
          color: "var(--beta-fg)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginTop: 1,
        }}
      >
        <MessageSquare size={15} />
      </span>
      <p style={{ margin: 0, flex: "1 1 auto", fontSize: 13, lineHeight: 1.45, color: "var(--fg-1)" }}>
        <span>
          <strong style={{ fontWeight: 700 }}>{BETA_NOTICE.lead}</strong>{" "}
          {BETA_NOTICE.body}
        </span>
        <br />
        <a
          href={`mailto:${BETA_NOTICE.feedbackEmail}`}
          style={{ color: "var(--accent-pressed)", fontWeight: 700, textDecoration: "underline" }}
        >
          {BETA_NOTICE.feedbackEmail}
        </a>
        .
      </p>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label={BETA_NOTICE.dismissLabel}
        style={{
          flexShrink: 0,
          width: 28,
          height: 28,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
          border: "1px solid var(--border-subtle)",
          borderRadius: 6,
          background: "var(--ink-50)",
          color: "var(--fg-3)",
          cursor: "pointer",
          marginTop: 1,
        }}
      >
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  );
}
